/*
Description: This is the file that will handle interacting with the Practice Pro EMR (app.ptpracticepro.com)
Notes:
    All Injectors need the following functions:
        getPageInfo
        getPatientInfo
        getQuestions
        answerQuestions (Consider combining these and making the single a private function)
        answerQuestion
*/

export function getPageInfo() {
  // function getPageInfo() {
  /*
        Description: This function will get the page info from the note
        Return: A dictionary with the page info
            {
                "URL": Base URL for the page,
                "Note Type": "PT Daily/SOAP Note",...
            }
        */
  const baseURL = window.location.origin;
  let noteType;
  let parentElement = document.querySelector(
    '.k-dropdown-wrap.k-state-default'
  );
  if (parentElement) {
    let spanElements = parentElement.querySelectorAll('span.k-state-default');
    if (spanElements.length > 0) {
      let firstSpanElement = spanElements[0];

      // Check if the first span element has only the class "k-state-default"
      if (
        firstSpanElement.classList.length === 1 &&
        firstSpanElement.classList.contains('k-state-default')
      ) {
        noteType = firstSpanElement?.innerText.trim();
      }
    }
  }
  return {
    URL: baseURL,
    'Note Type': noteType || 'SOAP NOTES'
  };
}

export function getPatientInfo() {
  // function getPatientInfo() {
  /*
        Description: This function will get the patient info from the note
        Return: A dictionary with the patient info
        {
            "EMR ID": 0x1234 or "michalskij"
            "First Name":firstName,
            "Last Name": lastName,
            "DOB": dob,
        }
        */
  // Assuming the element's text is "Halper John, he/him (EMR ID: 42332797)"
  let patientName = document.querySelector('#pat-avatar-name');
  const fullName = patientName.innerHTML.split(',');
  // Extract and trim the last name and first name
  const lastName = fullName[0].trim();
  const firstName = fullName[1].trim();
  const emrID = `${lastName},${firstName}`.replace(/\s/g, '');
  const dob = '05/29/2023';
  return {
    'EMR ID': emrID,
    'First Name': firstName,
    'Last Name': lastName,
    DOB: dob
  };
}

/* 
  questions - This is where we will store the questions
            - a list of dictionaries
       [{
          "Question": "Where was the injury?", 
          "Type": "Multiple Choice", 
          "Choices": ["Back", "Neck", "Shoulder"]
       },
          ...
          ]
   
  */

/*
  question Interactions - This is where we will store the interactions for each question
                          - a dictionary of dictionaries
           {
              "Where was the injury?": {
                  "Type": "Multiple Choice",
                  "Choices"{
                      "Back": () => {click the back button},
                      "Neck": () => {click the neck button},
                      "Shoulder": () => {click the shoulder button}
                  }
              },
          },
          {
              "What is the injury?": {
                  "Type": "Short Answer",
                  "Input": (val) => {input the value into the input box}
          }
              ...
           }
  */
var questionInteractions = {};
var questions = [];

// ─── Treatment Flowsheet ────────────────────────────────────────────────────

// Set an input value and trigger Angular's ng-model change detection
function setInputValue(input, value) {
  input.focus();
  input.value = value;
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
  input.blur();
}

// DOM helpers — plain selectors match both the category and treatment ui-selects
function getCategoryToggle() {
  return document.querySelector('[aria-label="Choose a category activate"]');
}

function normalizeTreatmentValue(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/-/g, ' ');
}

function getTreatmentLookupKeys(value) {
  var raw = getTreatmentLabelFromValue(value);
  if (!raw) return [];

  var variants = new Set();

  // Practice Pro appends UI-only suffixes like "+" and billing codes after the
  // row is added, so normalize a few display variants before comparing names.
  function addVariant(label) {
    var normalized = normalizeTreatmentValue(label)
      .replace(/\s+\+\s*$/, '')
      .replace(/\s+\d{4,5}\s*$/, '')
      .trim();
    if (normalized) variants.add(normalized);
  }

  addVariant(raw);
  addVariant(getTreatmentSearchTerm(raw));

  return Array.from(variants);
}

function treatmentNamesMatch(left, right) {
  var leftKeys = getTreatmentLookupKeys(left);
  var rightKeys = getTreatmentLookupKeys(right);

  for (var i = 0; i < leftKeys.length; i++) {
    if (rightKeys.includes(leftKeys[i])) return true;
  }

  return false;
}

function getTreatmentLabelFromValue(value) {
  if (value == null) return '';
  if (
    typeof value === 'object' &&
    !Array.isArray(value) &&
    ('Existing Value' in value || 'Description' in value || 'Choices' in value)
  ) {
    return getTreatmentLabelFromValue(value['Existing Value']);
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    return (
      value.Description ||
      value.description ||
      value.treatmentName ||
      value.name ||
      value.value ||
      value.label ||
      value.text ||
      ''
    )
      .toString()
      .trim();
  }

  var raw = String(value).replace(/\s+/g, ' ').trim();
  if (!raw) return '';

  var descMatch = raw.match(/Description:\s*(.+)$/i);
  if (descMatch && descMatch[1]) {
    return descMatch[1].trim();
  }

  return raw;
}

function normalizeTreatmentAnswerRow(row) {
  if (!row || typeof row !== 'object' || Array.isArray(row)) return row;
  return Object.fromEntries(
    Object.entries(row).map(function ([key, value]) {
      if (
        value &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        ('Existing Value' in value ||
          'Description' in value ||
          'Choices' in value)
      ) {
        return [key, value['Existing Value'] ?? null];
      }
      return [key, value];
    })
  );
}

function normalizeTreatmentsAnswer(answer) {
  if (!answer || typeof answer !== 'object') return answer;

  var raw =
    answer &&
    typeof answer === 'object' &&
    !Array.isArray(answer) &&
    Array.isArray(answer.answer) &&
    !('Treatment' in answer)
      ? answer.answer
      : answer;

  if (Array.isArray(raw)) {
    return Object.fromEntries(
      raw
        .map(function (row, index) {
          return [String(index), normalizeTreatmentAnswerRow(row)];
        })
        .filter(function ([, row]) {
          return row && typeof row === 'object';
        })
    );
  }

  if ('Treatment' in raw) {
    return { 0: normalizeTreatmentAnswerRow(raw) };
  }

  return Object.fromEntries(
    Object.entries(raw).map(function ([key, value]) {
      return [key, normalizeTreatmentAnswerRow(value)];
    })
  );
}

function sanitizeTreatmentListItemLabel(value) {
  var label = getTreatmentLabelFromValue(value);
  if (!label) return '';
  return label.replace(/\s+/g, ' ').trim();
}

function getTreatmentSearchTerm(value) {
  var label = sanitizeTreatmentListItemLabel(value);
  if (!label) return '';
  return label.replace(/\s*\([^()]*\)\s*$/, '').trim();
}

// ─── Treatment Plan Tab Navigation ─────────────────────────────────────────

/**
 * Returns all treatment plan tabs from the flowsheet tab bar.
 * Each entry: { name, tplid, isActive, li }
 * Browser-console usage: getTreatmentPlanTabs()
 */
function getTreatmentPlanTabs() {
  var tabItems = Array.from(document.querySelectorAll('.pp-fs-tabs li'));
  return tabItems
    .map(function (li) {
      // Tab name lives in .pp-fs-tab-name span; fall back to full li text
      var nameEl = li.querySelector('.pp-fs-tab-name');
      var link = li.querySelector('a');
      var href = link ? link.getAttribute('href') || '' : '';
      // tplid appears as ?tplid=NNN or /tplid/NNN in the anchor href
      var tplidMatch = href.match(/tplid[=/](\d+)/i);
      return {
        name: nameEl
          ? nameEl.textContent.trim()
          : li.textContent.trim().replace(/\s+/g, ' '),
        tplid: tplidMatch ? parseInt(tplidMatch[1], 10) : null,
        isActive: li.classList.contains('active'),
        li: li
      };
    })
    .filter(function (t) {
      return t.name;
    });
}

// ─── End Treatment Plan Tab Navigation ─────────────────────────────────────

// Field descriptions used in the Flex structure (shared between rows)
var TREATMENT_FIELD_DESCS = {
  Time: 'Extract treatment durations only if explicitly stated in the transcript. Do not infer or estimate. Return all durations in HH:MM:SS format.',
  Sets: 'Number of sets (numeric string, e.g. "3"). Derive from transcript if not applicable.',
  Reps: 'Number of reps (numeric string, e.g. "10"). Derive from transcript if not applicable.',
  Weight:
    'Weight used (numeric string, e.g. "10"). Derive from transcript if not applicable.',
  Tension:
    'Resistance band tension (e.g. "Red"). Derive from transcript if not applicable.',
  'Treatment Notes': 'Any additional notes for this treatment row.'
};

function buildTreatmentPlaceholderRow() {
  // Keep Treatments valid for Flex questions even when the table starts empty.
  return {
    Time: {
      'Existing Value': '',
      Description: TREATMENT_FIELD_DESCS.Time
    },
    Sets: {
      'Existing Value': '',
      Description: TREATMENT_FIELD_DESCS.Sets
    },
    Reps: {
      'Existing Value': '',
      Description: TREATMENT_FIELD_DESCS.Reps
    },
    Weight: {
      'Existing Value': '',
      Description: TREATMENT_FIELD_DESCS.Weight
    },
    Tension: {
      'Existing Value': '',
      Description: TREATMENT_FIELD_DESCS.Tension
    },
    'Treatment Notes': {
      'Existing Value': '',
      Description: TREATMENT_FIELD_DESCS['Treatment Notes']
    }
  };
}

/**
 * Read all current treatment rows from the PP flowsheet.
 * Returns an object keyed by index: { "0": { Treatment, Sets, Reps, Weight, Tension, "Treatment Notes" }, ... }
 */
// Row layout: TD[0]=checkbox-cell, TD[1]=name, TD[2]=time, TD[3]=sets, TD[4]=reps, TD[5]=weight, TD[6]=tension, ...
// Input layout (all inputs in the row): [0]=checkbox, [1]=time, [2]=sets, [3]=reps, [4]=weight, [5]=tension
function getTreatmentRowByName(name) {
  var tables = Array.from(
    document.querySelectorAll('.pp-fs-flowsheetTreatmentTable')
  );
  for (var ti = 0; ti < tables.length; ti++) {
    var trs = Array.from(tables[ti].querySelectorAll('tr.row-treatment'));
    for (var ri = 0; ri < trs.length; ri++) {
      var tds = trs[ri].querySelectorAll('td');
      if (tds[1] && treatmentNamesMatch(tds[1].textContent || '', name)) {
        return trs[ri];
      }
    }
  }
  return null;
}

function getExistingTreatmentRows() {
  var result = {};
  var tables = Array.from(
    document.querySelectorAll('.pp-fs-flowsheetTreatmentTable')
  );
  tables.forEach(function (tbl) {
    Array.from(tbl.querySelectorAll('tr.row-treatment')).forEach(function (tr) {
      // Skip rows whose ng-repeat ancestor is hidden
      var ngRepeatRow = tr.closest('tr.ng-scope');
      if (ngRepeatRow && ngRepeatRow.classList.contains('ng-hide')) return;
      var tds = tr.querySelectorAll('td');
      var name = tds[1] ? (tds[1].textContent || '').trim() : '';
      if (!name) return;
      var displayName = name
        .replace(/\s+/g, ' ')
        .replace(/\s*\+\s*$/, '')
        .trim();
      var inps = Array.from(tr.querySelectorAll('input'));
      var noteInput = tr.querySelector('textarea.input-note, textarea');
      // Include all rows present in the table regardless of checkbox state.
      // The AI fills in values for whatever is already on the flowsheet.
      result[displayName] = {
        Time: {
          'Existing Value': inps[1] ? inps[1].value : '',
          Description: TREATMENT_FIELD_DESCS.Time
        },
        Sets: {
          'Existing Value': inps[2] ? inps[2].value : '',
          Description: TREATMENT_FIELD_DESCS.Sets
        },
        Reps: {
          'Existing Value': inps[3] ? inps[3].value : '',
          Description: TREATMENT_FIELD_DESCS.Reps
        },
        Weight: {
          'Existing Value': inps[4] ? inps[4].value : '',
          Description: TREATMENT_FIELD_DESCS.Weight
        },
        Tension: {
          'Existing Value': inps[5] ? inps[5].value : '',
          Description: TREATMENT_FIELD_DESCS.Tension
        },
        'Treatment Notes': {
          'Existing Value': noteInput ? noteInput.value : '',
          Description: TREATMENT_FIELD_DESCS['Treatment Notes']
        }
      };
    });
  });
  return result;
}

/**
 * After a treatment row is marked as performed, fill its numeric/text fields.
 * Row input layout: [0]=checkbox, [1]=time, [2]=sets, [3]=reps, [4]=weight, [5]=tension
 */
async function fillTreatmentRow(treatmentName, rowValues) {
  var targetRow = getTreatmentRowByName(treatmentName);
  if (!targetRow) {
    console.error('[fillTreatmentRow] Row not found for:', treatmentName);
    return;
  }

  var inps = Array.from(targetRow.querySelectorAll('input'));
  // Confirmed input layout per row (via ng-model inspection):
  //   [0] checkbox  tmt.sel        [1] text tmt.time
  //   [2] text      tmt.sets       [3] text tmt.reps
  //   [4] text      tmt.wt         [5] text tmt.tension
  //   [6] checkbox  tmt.bulkAction
  var indexMap = { Time: 1, Sets: 2, Reps: 3, Weight: 4, Tension: 5 };

  var fields = Object.keys(rowValues);
  for (var i = 0; i < fields.length; i++) {
    var field = fields[i];
    var value = rowValues[field];
    // Skip non-data fields, empty values, and all-zero time strings (default).
    if (!value || field === 'Treatment' || field === 'Category') continue;
    if (field === 'Time' && /^[0:]+$/.test(String(value))) continue;

    if (indexMap.hasOwnProperty(field)) {
      var inp = inps[indexMap[field]];
      if (inp) {
        setInputValue(inp, value);
        console.log('[fillTreatmentRow] set', field, '=', value);
      }
    } else if (field === 'Treatment Notes') {
      // Treatment notes live in a textarea instead of the row's numeric inputs.
      var notesInput = targetRow.querySelector('textarea.input-note, textarea');
      if (notesInput) {
        setInputValue(notesInput, value);
        console.log('[fillTreatmentRow] set', field, '=', value);
      }
    }
  }
}

/**
 * Process the AI's treatment answer object:
 *   { "0": { Treatment, Sets, Reps, Weight, Tension, "Treatment Notes" }, ... }
 */
async function answerTreatments(answer) {
  var sourceMap =
    answer?.answer &&
    typeof answer.answer === 'object' &&
    !Array.isArray(answer.answer)
      ? answer.answer
      : normalizeTreatmentsAnswer(answer) || {};

  for (var [key, row] of Object.entries(sourceMap)) {
    var rowValues = normalizeTreatmentAnswerRow(row);
    if (!rowValues || typeof rowValues !== 'object') continue;

    var treatmentName = isEmptyOrNull(rowValues['Treatment'])
      ? key
      : rowValues['Treatment'];
    if (isEmptyOrNull(treatmentName)) continue;

    console.log('[PP] Filling treatment row: ' + treatmentName);
    await fillTreatmentRow(treatmentName, rowValues);
  }
}
// ─── End Treatment Flowsheet ────────────────────────────────────────────────

// ─── Goals Section ──────────────────────────────────────────────────────────

function createGoalTextInput(
  el: HTMLTextAreaElement | HTMLInputElement | null
) {
  return {
    'Existing Value': el ? el.value.trim() : '',
    Input: (val) => {
      if (el && !isEmptyOrNull(val)) setInputValue(el, val);
    }
  };
}

function createKendoDropdownInput(caseGoalId, currentValue) {
  var choices = [];
  // Prefer the Kendo widget's data source — authoritative and page-defined.
  var jq = (window as any).jQuery;
  if (jq) {
    var widget = jq('#ddlprogress_' + caseGoalId).data('kendoDropDownList');
    if (widget) {
      choices = widget.dataSource
        .data()
        .toJSON()
        .map(function (item) {
          return String(item);
        })
        .filter(function (s) {
          return s !== '';
        });
    }
  }
  // Fallback: read from the rendered Kendo listbox <li> elements.
  if (choices.length === 0) {
    var listbox = document.getElementById(
      'ddlprogress_' + caseGoalId + '_listbox'
    );
    if (listbox) {
      choices = Array.from(listbox.querySelectorAll('li'))
        .map(function (li) {
          return li.textContent.trim();
        })
        .filter(function (s) {
          return s !== '';
        });
    }
  }
  return {
    'Existing Value': currentValue,
    Choices: choices,
    Input: (val) => {
      if (isEmptyOrNull(val) || !choices.includes(val)) return;
      var jq2 = (window as any).jQuery;
      if (jq2) {
        var w = jq2('#ddlprogress_' + caseGoalId).data('kendoDropDownList');
        if (w) {
          w.value(val);
          w.trigger('change');
          return;
        }
      }
      var input = document.getElementById(
        'ddlprogress_' + caseGoalId
      ) as HTMLInputElement;
      if (input) setInputValue(input, val);
    }
  };
}

function getGoalRows() {
  var result = [];

  Array.from(document.querySelectorAll('table.goalChildGrid')).forEach(
    function (table) {
      var caseGoalIdInput = table.querySelector<HTMLInputElement>(
        'input[id*="CaseGoalId"]'
      );
      if (!caseGoalIdInput) return;
      var caseGoalId = caseGoalIdInput.value;

      var goalTextarea = table.querySelector<HTMLTextAreaElement>(
        'textarea[id*="GoalText"]'
      );
      var goalText = goalTextarea ? goalTextarea.value.trim() : '';

      var notesTextarea = document.getElementById(
        'txtnote_' + caseGoalId
      ) as HTMLTextAreaElement;
      var timeframeInput = document.getElementById(
        'txttimefream_' + caseGoalId
      ) as HTMLInputElement;
      var progressSpan = table.querySelector<HTMLElement>(
        '.k-dropdown .k-input'
      );
      var progressValue = progressSpan ? progressSpan.textContent.trim() : '';

      // Practice Pro uses multiple thead/tbody pairs inside one table#GoalGrid.
      // Find the tbody containing this goalChildGrid, then read its preceding thead's label.
      var containingTbody = table.closest('tbody');
      var prevSibling = containingTbody
        ? containingTbody.previousElementSibling
        : null;
      var categoryLabel =
        prevSibling && prevSibling.tagName === 'THEAD'
          ? prevSibling
              .querySelector<HTMLElement>('label')
              ?.textContent?.trim() || 'Goals'
          : 'Goals';
      var goalLabel = categoryLabel;

      result.push({
        [goalLabel]: {
          'Existing Value': goalText,
          Description:
            'If Goal mentioned return this exact text unchanged else give the new goal.',
          Input: (val: string) => {
            if (goalTextarea && !isEmptyOrNull(val))
              setInputValue(goalTextarea, val);
          }
        },
        Notes: {
          ...createGoalTextInput(notesTextarea),
          Description: `Additional notes for ${goalText} goal. Derive from the transcript.`
        },
        Timeframe: {
          ...createGoalTextInput(timeframeInput)
        },
        Progress: {
          ...createKendoDropdownInput(caseGoalId, progressValue)
        }
      });
    }
  );

  return result;
}

// ─── End Goals Section ──────────────────────────────────────────────────────

export async function getQuestions() {
  questions = [];
  questionInteractions = {};

  // Read existing treatment rows once to populate the question structure.
  const hasExistingTableRows = getExistingTreatmentRows();

  // Select the ul element containing the categories
  let ulElement = document.querySelector('.soapnote-tabs');
  if (ulElement) {
    // Select all the li elements within the ul
    let liElements = ulElement.querySelectorAll('li');

    // Array to store categories
    let categories = [];

    // Iterate over each li element
    liElements.forEach(function (li) {
      // Get the span element within the li
      let spanElement = li.querySelector('span.k-link');
      if (!spanElement) return;
      // Get the text content of the span element
      let category = spanElement.textContent.trim();
      // Add the category to the array
      categories.push(category);
    });

    // Get the main container
    let mainContainer = document.getElementById('SOAPCategoryTab');

    // Iterate through all the tabs under the main container
    for (let i = 1; i <= mainContainer.childElementCount; i++) {
      // Construct the id for the tab
      let tabId = 'SOAPCategoryTab-' + i;

      // Get the tab element
      let tabElement = document.getElementById(tabId);

      // Check if the tab element exists and if aria-expanded is true
      if (tabElement && tabElement.getAttribute('aria-expanded') === 'true') {
        // Extract and log the ValueTextBox textareas for this tab
        extractValueTextBoxForTab(tabElement, categories[i - 1]);
      } else {
        console.log('Tab ID: ' + tabId + ' does not exist or is not expanded.');
      }
    }
  } else {
    getSoapQuestions();
  }

  // Register only the treatment table for the currently active plan tab.
  // Keep the key stable as TREATMENTS across plan tabs so downstream templates
  // see one treatment-table shape instead of plan-specific variants.
  if (getCategoryToggle()) {
    let questionKey = 'TREATMENTS';

    // Re-use the rows already fetched above (avoids a second DOM scan).
    let existingRows = hasExistingTableRows;
    if (Object.keys(existingRows).length === 0) {
      existingRows = [buildTreatmentPlaceholderRow()];
    }

    questionInteractions[questionKey] = {
      Type: 'Flex',
      Question: questionKey,
      Key: questionKey,
      Structure: existingRows,
      Input: async (answer) => {
        await answerTreatments(answer);
      }
    };
  }

  // Register goals questions if the Goals grid is present on the page.
  // Group rows by category so each category gets its own Flex question.
  if (document.querySelector('table.goalGrid')) {
    const goalRows = getGoalRows();
    if (goalRows.length > 0) {
      // Build a map: categoryLabel → rows[]
      // The goal key in each row is the one that isn't Notes/Timeframe/Progress.
      var goalsByCategory: Record<string, typeof goalRows> = {};
      var RESERVED_FIELDS = new Set(['Notes', 'Timeframe', 'Progress']);
      goalRows.forEach(function (row) {
        var goalKey = Object.keys(row).find(function (k) {
          return !RESERVED_FIELDS.has(k);
        });
        if (!goalKey) return;
        // Strip trailing digits to recover the category label (e.g. "Goals0" → "Goals")
        var category = goalKey.replace(/\d+$/, '');
        if (!goalsByCategory[category]) goalsByCategory[category] = [];
        goalsByCategory[category].push(row);
      });

      Object.keys(goalsByCategory).forEach(function (category) {
        var rows = goalsByCategory[category];
        questionInteractions[category] = {
          Type: 'Flex',
          Question: category,
          Key: category,
          Structure: rows,
          Input: async (answer: any) => {
            var answerRows = Array.isArray(answer)
              ? answer
              : Array.isArray(answer?.answer)
                ? answer.answer
                : Object.values(answer || {});
            for (var i = 0; i < answerRows.length; i++) {
              var rowAnswer = answerRows[i];
              var structure = rows[i];
              if (!structure || !rowAnswer || typeof rowAnswer !== 'object')
                continue;
              for (var field of Object.keys(rowAnswer)) {
                if (
                  structure[field]?.Input &&
                  !isEmptyOrNull(rowAnswer[field])
                ) {
                  await structure[field].Input(rowAnswer[field]);
                }
              }
            }
          }
        };
      });
    }
  }

  // Fill the questions array with the questions
  Object.keys(questionInteractions).forEach((key) => {
    questions.push(questionInteractions[key]);
  });

  return { questions };
}

function getSoapQuestions() {
  // Select all fieldsets within .pp-fs-soapNotes
  let fieldsets = document.querySelectorAll('.pp-fs-soapNotes fieldset');

  // Iterate through each fieldset to get the legend (heading) and the textarea
  fieldsets.forEach(function (fieldset) {
    let heading = fieldset.querySelector('legend')?.textContent?.trim();
    let initialTextarea = fieldset.querySelector('textarea');

    if (initialTextarea) {
      let textareaId = initialTextarea.id;
      let finalTextarea = document.getElementById(textareaId);

      // Store the information in questionInteractions
      if (!questionInteractions[heading]) {
        questionInteractions[heading] = {
          Type: 'Long Answer',
          Question: heading,
          Key: heading,
          ExistingNote: finalTextarea.value.trim(),
          Input: async (val, replyType) => {
            if (isEmptyOrNull(val)) {
              return;
            }
            if (replyType === 'append') {
              finalTextarea.value = (finalTextarea.value + '\n' + val).trim();
            } else {
              finalTextarea.value = val;
            }
            // Trigger the 'change' event if needed
            var event = new Event('change', { bubbles: true });
            finalTextarea.dispatchEvent(event);
          }
        };
      }
    }
  });
}

async function answerLongAnswer(localSubQuestionTextArea, val, replyType) {
  try {
    if (isEmptyOrNull(val)) {
      return;
    }
    // Actually Type
    if (replyType === 'append') {
      setReactTextareaValue(
        localSubQuestionTextArea,
        localSubQuestionTextArea.value + '\n' + val
      );
    } else if (replyType === 'overwrite') {
      setReactTextareaValue(localSubQuestionTextArea, val);
    }
  } catch (error) {
    getLogFromInjector();
  }
}

function isEmptyOrNull(value) {
  return (
    value === null || value === undefined || value === '' || value === 'None'
  );
}

// Function to extract ValueTextBox textarea for a given tab element
function extractValueTextBoxForTab(tabElement, categoryName) {
  // Get all question divs within this tab
  let questionDivs = tabElement.querySelectorAll(
    '#frmtemp > div > table > tbody > tr:nth-child(2) > td.question > div'
  );

  if (
    questionDivs &&
    questionDivs.length > 0 &&
    questionDivs[0].classList?.contains('textarea')
  ) {
    // Iterate through each question div
    questionDivs.forEach(function (questionDiv, index) {
      // Get the textarea within the question div
      let textArea = questionDiv.querySelector('textarea');

      // Check if the textarea exists
      if (textArea) {
        // Store the question interaction
        questionInteractions[categoryName] = {
          Type: 'Long Answer',
          Question: categoryName,
          Key: categoryName,
          ExistingNote: textArea.value.trim(),
          Input: async (val, replyType) => {
            await answerLongAnswer(textArea, val, replyType);
          }
        };
      }
    });
  } else {
    // Get all question rows within this tab
    let questionRows = tabElement.querySelectorAll('tr');

    // Iterate through each question row
    questionRows.forEach(function (questionRow) {
      // Check if the current row is a QuestionHeaderRow
      let questionHeaderDiv = questionRow.querySelector('.QuestionHeaderRow');

      if (!questionHeaderDiv || !questionHeaderDiv.textContent.trim()) return;

      let sectionName = questionHeaderDiv.textContent.trim();
      let nextRow = questionRow.nextElementSibling as HTMLTableRowElement;
      if (!nextRow) return;

      // Determine control type from hidden input
      let controlTypeInput = nextRow.querySelector(
        'input[id$="__Controls_0__Type"]'
      ) as HTMLInputElement;
      let controlType = controlTypeInput?.value?.toLowerCase() || '';

      // The td.question holds all structured controls
      let questionTd = nextRow.querySelector('td.question');

      // The comment textarea is always in the last td of the content row
      let commentTextarea = nextRow.querySelector(
        '.txtComment'
      ) as HTMLTextAreaElement;

      // Build a Flex structure for this section
      let structure: Record<string, any> = {};

      if (controlType === 'textbox') {
        // Simple textarea — treat as a writable Value field
        let textarea = questionTd?.querySelector(
          'textarea'
        ) as HTMLTextAreaElement;
        if (textarea) {
          structure[sectionName] = {
            'Existing Value': textarea.value.trim(),
            Input: async (val, replyType = 'overwrite') => {
              await answerLongAnswer(textarea, val, replyType);
            }
          };
        }
      } else if (controlType === 'radiobutton') {
        // Single-select radio buttons
        let radioInputs = Array.from(
          questionTd?.querySelectorAll('input[type="radio"]') || []
        ) as HTMLInputElement[];
        let choiceLabels: string[] = [];
        let existingValue = '';
        radioInputs.forEach(function (radio) {
          let label = questionTd
            .querySelector(`label[for="${radio.id}"]`)
            ?.textContent?.trim();
          if (label) {
            if (radio.checked) existingValue = label;
            choiceLabels.push(label);
          }
        });
        if (choiceLabels.length > 0) {
          structure[sectionName] = {
            Description: `Select one option for ${sectionName}.`,
            Choices: choiceLabels,
            Input: (val) => {
              let target = radioInputs.find(
                (r) =>
                  questionTd
                    .querySelector(`label[for="${r.id}"]`)
                    ?.textContent?.trim() === val
              );
              if (target) target.click();
            }
          };
        }
      } else if (controlType === 'checkboxlist') {
        // Multi-select checkboxes
        let checkboxInputs = Array.from(
          questionTd?.querySelectorAll(
            'input[type="checkbox"][id^="ValueCheckBox_"]'
          ) || []
        ) as HTMLInputElement[];
        let choiceLabels: string[] = [];
        let existingValues: string[] = [];
        checkboxInputs.forEach(function (checkbox) {
          let label = questionTd
            .querySelector(`label[for="${checkbox.id}"]`)
            ?.textContent?.trim();
          if (label) {
            if (checkbox.checked) existingValues.push(label);
            choiceLabels.push(label);
          }
        });
        if (choiceLabels.length > 0) {
          structure[sectionName] = {
            Description: `Select all applicable answers. If none apply, reply with 'None'. Provide your response as a list of choices separated by a '$' symbol. Valid options to choose from: ${choiceLabels.join(', ')}`,
            Input: (selectedOptions) => {
              checkboxInputs.forEach((checkbox) => {
                let label = questionTd
                  .querySelector(`label[for="${checkbox.id}"]`)
                  ?.textContent?.trim();
                if (label?.toLowerCase() === 'none') return;
                if (
                  label &&
                  selectedOptions.includes(label) &&
                  !checkbox.checked
                ) {
                  checkbox.click();
                }
              });
            }
          };
        }
      } else if (controlType === 'soaptable') {
        // Structured table — one field per row×column cell
        let soapTable = questionTd?.querySelector('table.ptpro_table');
        if (soapTable) {
          let headerCells = soapTable.querySelectorAll('thead tr th');
          // Skip the first header (row-label column, usually &nbsp;)
          let colHeaders = Array.from(headerCells)
            .map((th) => th.textContent.trim())
            .slice(1);

          soapTable.querySelectorAll('tbody tr').forEach(function (row) {
            let cells = row.querySelectorAll('td');
            if (cells.length === 0) return;
            let rowLabel = cells[0].textContent.trim();
            if (!rowLabel) return;

            for (let colIndex = 1; colIndex < cells.length; colIndex++) {
              let colHeader = colHeaders[colIndex - 1] || `Col${colIndex}`;
              let input = cells[colIndex].querySelector(
                'input[type="text"]'
              ) as HTMLInputElement;
              if (!input) continue;

              let fieldKey = `${rowLabel} - ${colHeader}`;
              let maxLen = input?.maxLength > 0 ? input.maxLength : null;
              structure[fieldKey] = {
                'Existing Value': input.value.trim(),
                ...(maxLen !== null && {
                  Description: `Short answer — word, number, or brief sentence (max ${maxLen} characters).`
                }),
                Input: async (val) => {
                  if (isEmptyOrNull(val)) return;
                  // Unwrap if the AI returned the full field config object
                  let strVal =
                    val && typeof val === 'object'
                      ? val['Existing Value'] ?? val['answer'] ?? ''
                      : val;
                  if (isEmptyOrNull(strVal, input.value)) return;
                  input.value = String(strVal);
                  var changeEvent = new Event('change', { bubbles: true });
                  input.dispatchEvent(changeEvent);
                }
              };
            }
          });
        }
      }

      // Comment field — always optional, AI can skip it
      if (commentTextarea) {
        structure['Comment'] = {
          'Existing Value': commentTextarea.value.trim(),
          Input: async (val, replyType = 'overwrite') => {
            if (isEmptyOrNull(val)) return;
            await answerLongAnswer(commentTextarea, val, replyType);
          }
        };
      }

      if (Object.keys(structure).length === 0) return;

      // Wrap in a two-level structure so from_comprehend_to_openai processes
      // fields (Value, Comment, …) as schema properties — not their metadata keys.
      // AI returns: { [sectionName]: { [sectionName]: "...", Comment: "..." } }
      let questionKey = sectionName + ' - ' + categoryName;
      questionInteractions[questionKey] = {
        Type: 'Flex',
        Question: questionKey,
        Key: questionKey,
        Structure: { [sectionName]: structure },
        Input: async (answer, replyType: string) => {
          if (!answer || typeof answer !== 'object') return;
          // Unwrap the top-level section key; fall back to answer itself if absent
          let fieldValues =
            (answer[sectionName] as Record<string, any>) ?? answer;
          for (const [field, value] of Object.entries(fieldValues)) {
            if (structure[field]?.Input && !isEmptyOrNull(value as string)) {
              await structure[field].Input(value, replyType);
            }
          }
        }
      };
    });
  }
}

async function setReactTextareaValue(editorElement, value) {
  // Create and dispatch a click event
  const clickEvent = new Event('click', { bubbles: true });
  editorElement.dispatchEvent(clickEvent);

  // Create and dispatch a focus event
  const focusEvent = new Event('focus', { bubbles: true });
  editorElement.dispatchEvent(focusEvent);

  // Create and dispatch a focusin event
  const focusinEvent = new Event('focusin', { bubbles: true });
  editorElement.dispatchEvent(focusinEvent);

  // Update the value
  editorElement.value = value;

  // Create and dispatch a change event
  const changeEvent = new Event('change', { bubbles: true });
  editorElement.dispatchEvent(changeEvent);

  // Create and dispatch a blur event
  const blurEvent = new Event('blur', { bubbles: true });
  editorElement.dispatchEvent(blurEvent);

  // Create and dispatch a focusout event
  const focusoutEvent = new Event('focusout', { bubbles: true });
  editorElement.dispatchEvent(focusoutEvent);

  // Create and dispatch a submit event
  const submitEvent = new Event('submit', { bubbles: true });
  editorElement.dispatchEvent(submitEvent);
}

export function answerQuestions(answers) {
  // function answerQuestions(answers) {
  /*
        Description: This function will take the answers and populate the questions
        
        Args: 
            answers: A dictionary of answers
                "Question": "The question in question"
                "Answer": "The answer to the question"
       
        Return: A list of any questions that were not answered, perhaps the answer didn't match the questions expected or whatnot...
            [
                {
                    Question: "Where was the injury?", 
                    Answer: "Back" 
                    Error: "The error message" (Choice not found, etc)
                },
                ...
            }
    
        */
  var errors = [];
  answers.forEach((answer) => {
    var error = answerQuestion(answer['Question'], answer['Answer']);
    if (error) {
      errors.push({
        Question: answer['Question'],
        Answer: answer['Answer'],
        Error: error
      });
    }
  });
  console.error('Errors:');
  console.error(errors);
  return errors;
}

export async function answerQuestion(question, answer, replyType) {
  // function answerQuestion(question, answer) {
  /*
        Description: This function will take the answer and populate the question
        
        Args: 
            question: The question in question
            answer: The answer to the question
       
        Return: null if success otherwise a string with the error message
        */

  // console.log("Answering Question: '" + question + "' with '" + answer + "'");

  // Make sure the question exists
  if (questionInteractions[question] == null) {
    console.error('Question not found');
    console.error(question);
    // console.log(questionInteractions);
    // console.log(Object.keys(questionInteractions));

    return 'Question not found';
  }

  // If it is a short answer or long answer, just populate the input
  if (questionInteractions[question]['Input']) {
    await questionInteractions[question]['Input'](answer, replyType);
    return true;
  }

  // Check if the answer is a list of answers or just a single answer
  if (Array.isArray(answer)) {
    // Choose All
    var notFoundAnswers = [];
    for (var i = 0; i < answer.length; i++) {
      if (answer[i] in questionInteractions[question]['Choices']) {
        console.log('Answer found ' + answer[i]);
        questionInteractions[question]['Choices'][answer[i]](true);
      } else {
        notFoundAnswers.push(answer[i]);
      }
    }
    if (notFoundAnswers.length > 0) {
      console.error('The following answers were not found: ' + notFoundAnswers);
      return 'The following answers were not found: ' + notFoundAnswers;
    } else {
      return true;
    }
  } else {
    // Multiple Choice
    if (answer in questionInteractions[question]['Choices']) {
      questionInteractions[question]['Choices'][answer]();
      return true;
    } else {
      console.error('Answer not found');
      console.error('Question: ' + question);
      console.error('Answer:' + answer);
      console.error(questionInteractions[question]['Choices']);
      return 'Answer not found';
    }
  }
}

export async function getLogFromInjector() {
  try {
    const htmlContent = document.documentElement.outerHTML;
    chrome.runtime.sendMessage({
      type: 'INJECTOR_ERROR',
      error: { htmlContent: htmlContent }
    });
    return { htmlContent: htmlContent };
  } catch (error) {
    // Return the full HTML content of the document
    console.error('Error occurred while fetching document:', error);
    getLogFromInjector();
  }
}
